<template>
  <div class="card choice-card">
    <div class="card-header d-flex align-items-center">
      <span class="required">{{ title }}</span>
      <button
        v-if="exchange"
        class="btn btn-sm btn-success ms-auto"
        @click.prevent="$emit('show-select-exchange')"
      >
        {{ $t('application_form.change_choice') }}
      </button>
      <button
        v-if="exchange && allowDelete"
        class="btn btn-sm btn-danger ms-2"
        @click.prevent="$emit('delete-exchange')"
      >
        {{ $t('application_form.remove_choice') }}
      </button>
    </div>
    <div class="card-body">
      <div v-if="exchange">
        <div
          v-if="hasErrorMessages"
          class="alert alert-danger"
        >
          <h4>{{ $t('application_form.errors_title') }}</h4>
          <hr>
          <ul>
            <li
              v-for="(message, messageIdx) of exchange.errorMessages"
              :key="messageIdx"
            >
              {{ message }}
            </li>
          </ul>
        </div>
        <div class="row gx-2 exchange-details">
          <dl
            v-if="!withSubSpecialisations"
            class="col"
          >
            <dt>{{ $t('application_form.choice.country') }}</dt>
            <dd>{{ exchange.countryName }}</dd>
          </dl>
          <dl class="col">
            <dt>{{ $t('application_form.choice.public') }}</dt>
            <dd>{{ exchange.functions }}</dd>
          </dl>
          <dl class="col">
            <dt>{{ $t('application_form.choice.available_places') }}</dt>
            <dd>{{ exchange.availablePlaces }}</dd>
          </dl>
          <dl
            v-if="hasSpecialisation"
            class="col"
          >
            <dt>{{ $t('application_form.choice.specialisation') }}</dt>
            <dd>{{ exchange.specialisation }}</dd>
          </dl>
        </div>
        <hr>
        <div class="row gx-2">
          <div
            v-if="!withSubSpecialisations && exchange.exchangeTypeItems.length > 0"
            class="mb-3 col-4"
          >
            <label class="form-label required">{{ $t('application_form.choice.type') }}</label>
            <select
              v-model="exchange.exchangeTypeId"
              class="form-select form-select-sm"
            >
              <option
                v-for="exchangeType of exchange.exchangeTypeItems"
                :key="exchangeType.id"
                :value="exchangeType.id"
              >
                {{ exchangeType.name }}
              </option>
            </select>
          </div>
          <div
            v-if="exchange.languageItems.length > 0"
            class="mb-3 col-4"
          >
            <label
              :for="`${choiceKey}-language`"
              class="form-label"
              :class="{ required: !exchange.anyLanguageOnly }"
            >
              {{ $t('application_form.choice.language') }}
            </label>
            <input
              v-if="exchange.anyLanguageOnly"
              :id="`${choiceKey}-language`"
              :value="exchange.languageItems[0].name"
              type="text"
              class="form-control form-control-sm"
              readonly
            >
            <select
              v-else
              :id="`${choiceKey}-language`"
              v-model="exchange.languageId"
              class="form-select form-select-sm"
            >
              <option
                v-for="language of exchange.languageItems"
                :key="language.id"
                :value="language.id"
              >
                {{ language.name }}
              </option>
            </select>
          </div>
          <div
            v-if="exchange.durationItems.length > 0"
            class="mb-3 col-4"
          >
            <label class="form-label required">{{ $t('application_form.choice.duration') }}</label>
            <select
              v-model="exchange.durationId"
              class="form-select form-select-sm"
            >
              <option
                v-for="duration of exchange.durationItems"
                :key="duration.id"
                :value="duration.id"
              >
                {{ duration.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="row gx-2">
          <div class="col-12 mb-0">
            <label class="form-label">{{ $t('application_form.choice.preferred_period') }}</label>
            <input
              v-model="exchange.preferredPeriod"
              type="text"
              class="form-control form-control-sm"
            >
            <small class="form-text text-muted">{{ $t('application_form.choice.preferred_period_help') }}</small>
          </div>
        </div>
        <div
          v-if="exchange.allowPreMatchedParticipantToBeNamed"
          class="row gx-2 mt-3"
        >
          <div class="col-12 mb-0">
            <label
              :for="`${choiceKey}-pre-match-counterpart-name`"
              class="form-label"
            >
              {{ $t('application_form.choice.pre_match_counterpart_name') }}
            </label>
            <input
              :id="`${choiceKey}-pre-match-counterpart-name`"
              v-model="exchange.preMatchCounterpartName"
              type="text"
              class="form-control form-control-sm"
            >
          </div>
        </div>
        <div
          v-if="exchange.askAboutHostingMultipleCounterparts"
          class="row gx-2 mt-3"
        >
          <div class="col-12 mb-0">
            <div class="form-check">
              <input
                :id="`${choiceKey}-has-multiple-counterparts`"
                v-model="exchange.hasMultipleCounterparts"
                type="checkbox"
                class="form-check-input"
              >
              <label
                class="form-check-label"
                :for="`${choiceKey}-has-multiple-counterparts`"
              >
                {{ $t('application_form.choice.has_multiple_counterparts') }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="card-text text-muted text-center"><em>{{ $t('short_term_application.choice_not_set_required') }}</em>
        </p>
        <div class="text-center">
          <button
            type="button"
            class="btn btn-sm btn-info"
            @click.prevent="$emit('show-select-exchange')"
          >
            {{ $t('short_term_application.add_first_choice') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import {useTranslation} from 'i18next-vue'
import type {ExchangeChoice} from '../types'
import {isNonEmptyString} from '@/js/js-utils'

const {t: $t} = useTranslation()

defineProps({
  title: {type: String},
  allowDelete: {type: Boolean},
  choiceKey: {type: String, required: true},
})

const exchange = defineModel<ExchangeChoice | null>()

defineEmits<{
  'show-select-exchange': []
  'delete-exchange': []
}>()

const hasErrorMessages = computed((): boolean => {
  return Array.isArray(exchange.value?.errorMessages) && exchange.value.errorMessages.length > 0
})

const hasSpecialisation = computed((): boolean => {
  return isNonEmptyString(exchange.value?.specialisation)
})

const withSubSpecialisations = computed((): boolean => {
  return exchange.value?.withSubSpecialisations === true
})

</script>
